import { gql } from '@apollo/client';

export const SHOWROOM_MEDIA_INFO_FRAGMENT = gql`
	fragment MediaInfo on Media {
		id
		mediaType
		cloudinaryId
		cloudinaryVersion
		name
		globalDescription
		isSample
		extension
		width
		height
		... on Video {
			duration
		}
	}
`;

export const SHOWROOM_SECTION_INFO = gql`
	fragment ShowroomSectionInfo on ShowroomSection {
		id
		name
		rows {
			medias {
				...MediaInfo
			}
		}
		options {
			spacing
			fullWidth
			displayLabel
			zoomLayer
			productLayer
			productLayerSettings {
				galleriesFields
				samplesFields
			}
			globalSelectedFilters {
				galleriesFields
				samplesFields
			}
		}
	}
	${SHOWROOM_MEDIA_INFO_FRAGMENT}
`;

export const SHOWROOM_SECTION_INFO_WITH_ROWMEDIAIDS = gql`
	fragment ShowroomSectionInfoWithRowMediaIds on ShowroomSection {
		...ShowroomSectionInfo
		rowMediaIds {
			id
			mediaIds
		}
	}
	${SHOWROOM_SECTION_INFO}
`;

export const SHOWROOM_INFO = gql`
	fragment ShowroomInfo on Showroom {
		id
		name
		downloadEnabled
		downloadPdfEnabled
		requestSamplesEnabled
		samplesDepartmentId
		heroMedia {
			publicId
			publicUrl
			mediaType
		}
		displayHeroMedia
		displayHeroContent
		heroTitle
		heroContent
		heroFontColor
		heroBackgroundColor
		industry
		createdAt
		updatedAt
		language
		archive
		library {
			id
			logo {
				publicId
				publicUrl
			}
		}
		librarySeason
		libraryCollection
		tenant {
			id
			uuid
			name
			tenant
			EcosystemAccountID
			EcosystemAccountName
			salesforceAccountId
			salesforceAccountParentId
			logo {
				publicId
				publicUrl
			}
			isSamplesEnabled
		}
		hasSamples
		pdfs {
			mediaType
			publicId
			publicUrl
			originalFilename
			createdAt
		}
		prContacts {
			id
			name
			jobTitle
			email
			phone
		}
	}
`;

export const SHOWROOMS = gql`
	query getShowrooms($archive: Boolean) {
		getShowrooms(archive: $archive) {
			id
			name
			password
		}
	}
`;

export const GET_PUBLIC_SHOWROOM = gql`
	query getPublicShowroom($id: String!, $tenant: String, $password: String) {
		getShowroom(id: $id, tenant: $tenant, password: $password) {
			...ShowroomInfo
			sections {
				...ShowroomSectionInfoWithRowMediaIds
			}
		}
	}
	${SHOWROOM_INFO}
	${SHOWROOM_SECTION_INFO_WITH_ROWMEDIAIDS}
`;

export const GET_SHOWROOM_SECTIONS = gql`
	query getShowroomSections($id: String!, $tenant: String, $password: String) {
		getShowroom(id: $id, tenant: $tenant, password: $password) {
			sections {
				...ShowroomSectionInfoWithRowMediaIds
			}
		}
	}
	${SHOWROOM_SECTION_INFO_WITH_ROWMEDIAIDS}
`;

export const GET_PUBLIC_SHOWROOM_WITHOUT_SECTIONS = gql`
	query getPublicShowroomWithoutSections($id: String!, $tenant: String, $password: String) {
		getShowroom(id: $id, tenant: $tenant, password: $password) {
			...ShowroomInfo
		}
	}
	${SHOWROOM_INFO}
`;

export const GET_SHOWROOM = gql`
	query getShowroom($id: String!, $tenant: String, $password: String) {
		getShowroom(id: $id, tenant: $tenant, password: $password) {
			...ShowroomInfo
			password
			sections {
				...ShowroomSectionInfoWithRowMediaIds
			}
		}
	}
	${SHOWROOM_INFO}
	${SHOWROOM_SECTION_INFO_WITH_ROWMEDIAIDS}
`;

export const GET_SHOWROOM_WITHOUT_SECTIONS = gql`
	query getShowroomWithoutSections($id: String!, $tenant: String, $password: String) {
		getShowroom(id: $id, tenant: $tenant, password: $password) {
			...ShowroomInfo
			password
		}
	}
	${SHOWROOM_INFO}
`;

export const GET_SHOWROOM_PUBLIC_FIELDS = gql`
	query getPublicShowroomFields($id: String!, $tenant: String!) {
		publicShowroomFields(id: $id, tenant: $tenant) {
			id
			name
			language
			libraryCollection
			librarySeason
			heroMedia {
				mediaType
				publicId
				publicUrl
			}
			logo
			defaultBackground {
				publicUrl
				publicId
				mediaType
				... on ShowroomImage {
					width
					height
				}
			}
		}
	}
`;

export const REMOVE_SHOWROOM = gql`
	mutation deleteShowroom($showroomId: String!) {
		deleteShowroom(showroomId: $showroomId)
	}
`;

export const REMOVE_SHOWROOMS = gql`
	mutation deleteShowrooms($ids: [String]!) {
		deleteShowrooms(ids: $ids)
	}
`;

export const UPDATE_SHOWROOMS_FROM_LIST = gql`
	mutation updateShowroomsFromList($ids: [String]!, $params: ShowroomUpdateParams) {
		updateShowrooms(ids: $ids, params: $params) {
			id
			name
			password
		}
	}
`;

export const GENERATE_SHOWROOM_ARCHIVE = gql`
	mutation generateShowroomArchive($params: GenerateShowroomArchiveParams!) {
		generateShowroomArchive(params: $params) {
			id
		}
	}
`;

export const DUPLICATE_SHOWROOM = gql`
	mutation duplicateShowroom($showroomId: String!) {
		duplicateShowroom(showroomId: $showroomId) {
			id
			name
			password
			archive
		}
	}
`;

export const GET_SHOWROOM_SHARE_INFO = gql`
	query getShowroomShareInfo($showroomId: String!) {
		getShowroom(id: $showroomId) {
			id
			name
			password
		}
	}
`;

export const GET_SHOWROOM_DOWNLOAD_ENABLED = gql`
	query getShowroomDownloadEnabled($showroomId: String!) {
		getShowroom(id: $showroomId) {
			id
			downloadEnabled
		}
	}
`;

export const GET_SHOWROOM_BASKET_INFO = gql`
	query getShowroomBasketInfo($showroomId: String!) {
		getShowroom(id: $showroomId) {
			id
			tenant {
				isSamplesEnabled
			}
			requestSamplesEnabled
			hasSamples
		}
	}
`;

export const GET_SHOWROOM_SECTION_FILTERS = gql`
	query getShowroomSectionFiltersUserView($params: ShowroomGetParams!, $section: Int!) {
		showroomSectionFilters(params: $params, section: $section) {
			id
			groups
			values {
				id
				groupId
				value
				mediaIds
			}
		}
	}
`;

export const SHOWROOM_FILTER_VALUE_INFO = gql`
	fragment ShowroomFilterValueInfo on ShowroomFilterValue {
		id
		groupId
		mediaIds
	}
`;

export const SHOWROOM_FILTER_GROUP_INFO = gql`
	fragment ShowroomFilterGroupsInfo on ShowroomFilter {
		id
		groups
	}
`;

export const GET_SHOWROOM_FILTER_VALUES = gql`
	query getShowroomSectionFilterValues($ids: [String]) {
		showroomFilterValues(ids: $ids) @client {
			...ShowroomFilterValueInfo
		}
	}
	${SHOWROOM_FILTER_VALUE_INFO}
`;

export const GET_SHOWROOM_PR_CONTACTS = gql`
	query getShowroomPrContacts($showroomId: String!) {
		getShowroom(id: $showroomId) {
			id
			prContacts {
				id
				name
				jobTitle
				email
				phone
			}
		}
	}
`;
